import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { brand, radius, semantic, space, typeRole } from '@repo/design-tokens';
import {
  Icon,
  NavBar,
  NavBarStatus,
  Tag,
  accountMenuLabel,
  type IconName,
} from '@repo/design-system';
import { AccountMenu } from '@repo/ui';
import type { OnboardingRoute } from '@repo/front-end-utils';
import twinlabsAiLogo from '@repo/assets/logos/twinlabs-ai-logo.svg';

import Config from '../../common/Config';

interface Props {
  className?: string;
  /**
   * The twin's route (`resolveOnboardingRoute`; `twin-details-page-landing`, R5). A chip tells the
   * operator at a glance whether they are standing up a new twin (*New twin*), updating a live one
   * (*Updating live twin*), or giving a live twin its first launch through the wizard (*First
   * launch*, the design's `CHIP.migrate`). `live-unread` — the launch record could not be read —
   * reads *Updating live twin*, which claims nothing about launches. Null or omitted while the twin
   * read is in flight, when it found nothing, and on the refusal screens: no chip. A static tag
   * unless `onOpenVersions` is given, when it is the Versions trigger (`twin-details-page-versions`).
   */
  route?: OnboardingRoute | null;
  /**
   * What the draft is, after the route label (`twin-details-page-versions`, R1.2): *Draft from v4*,
   * *Draft · not launched*. Null or omitted for none.
   */
  versionText?: string | null;
  /**
   * Makes the chip a button that opens Versions (R1). Omitted until a plan is loaded and the landing
   * has settled, and on the screens with no plan behind them: the chip is then the static tag.
   */
  onOpenVersions?: () => void;
  /** Versions is open: the trigger says so (`aria-expanded`) and shows its open glow. */
  versionsOpen?: boolean;
  /** The trigger, so focus can return to it when Versions closes or a swap finishes. */
  chipRef?: React.Ref<HTMLButtonElement>;
  /**
   * Asked before the logo button leaves the wizard. Resolving `false` cancels the exit and leaves
   * the operator exactly where they were.
   *
   * This button is the wizard's **only** in-app exit reachable with unsaved work — the four other
   * `navigate('/twin')` calls all sit after Go Live, where leaving is the intended outcome and a
   * warning would be noise. Optional, because the two pre-flight refusal screens render this header
   * with no plan behind it.
   */
  onBeforeLeave?: () => Promise<boolean>;
  /**
   * The twin's name, after the logo (frames 03, 06 and 10; `twin-details-page-shell`, R4). Omitted
   * while the twin read is in flight, when it found nothing, and on the two refusal screens — the
   * header then shows no name rather than a BID or a placeholder word.
   */
  twinName?: string;
  /**
   * The step's own controls in the bar's right slots, before the avatar: the Observers step's
   * heading, search, notices and save tag (`observer-autogen-review-layout`, L1). Omitted, the right
   * slots are empty.
   */
  actions?: React.ReactNode;
  /**
   * The step's title, after the chip (`onboarding-persistent-scene-step-titles`, R1.2): the step's
   * name, or the Launch label, or the hidden *Opening the twin* while the landing is pending.
   * Omitted on the refusal screens: the bar then shows no title.
   */
  stepTitle?: React.ReactNode;
  /**
   * One line under the title saying what the step is for or when its changes apply (R1.3). Null,
   * omitted or empty: no status line.
   */
  statusLine?: string | null;
  /** The title's heading, so a draft swap, *Next* and a created twin can put focus on it. */
  headingRef?: React.Ref<HTMLHeadingElement>;
  /**
   * The bar's surface (`spec-onboarding-persistent-scene-full-bleed`, R4.1): `glass` on the wizard,
   * where it floats over the page's backdrop; `on-solid`, the default, on the create route and the
   * refusal screens, where nothing is ever behind it. The chip's tag follows it.
   */
  surface?: 'glass' | 'on-solid';
}

/**
 * Each route's chip, as the design's `CHIP` table draws it (`Twin Details Page (standalone).html`):
 * `fresh` (*New twin*), `migrate` (*First launch*) and `live` (*Updating live twin*, also for a live
 * twin whose launch record could not be read — it claims nothing about launches).
 */
const CHIP: Record<
  OnboardingRoute,
  { label: string; tone: 'info' | 'warn'; icon: IconName; dot: boolean }
> = {
  create: { label: 'New twin', tone: 'info', icon: 'wand-magic-sparkles', dot: false },
  migrate: { label: 'First launch', tone: 'info', icon: 'arrows-rotate', dot: false },
  manage: { label: 'Updating live twin', tone: 'warn', icon: 'pen', dot: true },
  'live-unread': { label: 'Updating live twin', tone: 'warn', icon: 'pen', dot: true },
};

/**
 * The chip's own words for a route, or null while the route is not known. Read by the Equipment
 * step's drop-zone target (`equipment-page-start`, R4), so the two can never say different things.
 */
export const routeChipLabel = (route: OnboardingRoute | null | undefined): string | null =>
  route ? CHIP[route].label : null;

/**
 * The onboarding header: the design system's nav bar (`onboarding-persistent-scene-nav-bar`, R5) on
 * every screen that shows it — the wizard, the create route and the refusal screens. On the wizard
 * it is on its `glass` surface, floating over the page's backdrop
 * (`onboarding-persistent-scene-full-bleed`); elsewhere `on-solid`. 52 px tall and 8 px in from the
 * top and sides, where the viewer's bar sits. The brand leaves for `/twin` (asking `onBeforeLeave` first);
 * the left slots hold the twin's name, its chip or Versions trigger, then the step's title and its
 * status line (`onboarding-persistent-scene-step-titles`); the right slots hold the step's `actions`; and the avatar opens the signed-in person's account menu, where _Email support_
 * replaces the old "Stuck and need assistance?" block.
 */
const OnboardingHeader: React.FC<Props> = ({
  className,
  route,
  versionText,
  onOpenVersions,
  versionsOpen = false,
  chipRef,
  onBeforeLeave,
  twinName,
  actions,
  stepTitle,
  statusLine,
  headingRef,
  surface = 'on-solid',
}) => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const chip = route ? CHIP[route] : null;
  const tagSurface = surface === 'glass' ? 'on-glass' : 'on-solid';
  const name = user?.name?.trim() ? user.name : undefined;
  const email = user?.email || undefined;

  const leave = async (): Promise<void> => {
    if (onBeforeLeave && !(await onBeforeLeave())) return;
    navigate('/twin');
  };

  return (
    // Over the plan on the glass steps: a fly-to lands below it (`plan-fly-to`, R3).
    <header className={className} data-plan-cover="top">
      <NavBar
        surface={surface}
        aria-label="TwinLabs.ai"
        brand={{
          logoSrc: twinlabsAiLogo,
          logoAlt: 'Twinlabs.ai',
          name: 'TwinLabs.ai',
          label: 'Back to your twins',
          /*
           * `.catch` rather than a bare `void`: `leave()` awaits `onBeforeLeave`, a prop, so a
           * caller that throws would surface as an unhandled rejection with no trace of which
           * control caused it. Today's caller cannot — `useOnboardingModal`'s `confirmLeave` settles
           * rather than rejecting — but that is a different module's invariant to borrow.
           */
          onClick: () => {
            void leave().catch(err =>
              console.error('[onboarding] leaving the wizard failed:', err),
            );
          },
        }}
        left={
          twinName || chip || stepTitle ? (
            <>
              {twinName ? (
                <span className="twin-name" title={twinName}>
                  {twinName}
                </span>
              ) : null}
              {chip && onOpenVersions ? (
                <button
                  ref={chipRef}
                  type="button"
                  className={`chip-trigger${versionsOpen ? ' open' : ''}`}
                  aria-haspopup="dialog"
                  aria-expanded={versionsOpen}
                  aria-label={versionText ? `${chip.label}, ${versionText}` : chip.label}
                  onClick={onOpenVersions}
                >
                  <Tag
                    surface={tagSurface}
                    tone={chip.tone}
                    size="roomy"
                    dot={chip.dot}
                    icon={<Icon name={chip.icon} variant="solid" size="xs" />}
                  >
                    <span className="chip-content">
                      {chip.label}
                      {versionText ? (
                        <>
                          <span aria-hidden="true">·</span>
                          {versionText}
                        </>
                      ) : null}
                      <Icon name="chevron-down" variant="solid" size="xs" />
                    </span>
                  </Tag>
                </button>
              ) : chip ? (
                <Tag
                  surface={tagSurface}
                  tone={chip.tone}
                  size="roomy"
                  dot={chip.dot}
                  icon={<Icon name={chip.icon} variant="solid" size="xs" />}
                >
                  {chip.label}
                </Tag>
              ) : null}
              {stepTitle ? (
                /* The step's title and status line (R1): the hook the browser rigs read. */
                <span className="step-header" data-onboarding-step-header="">
                  {/* Focusable by script only: where a draft swap and Next leave the keyboard. */}
                  <h3 ref={headingRef} tabIndex={-1}>
                    {stepTitle}
                  </h3>
                  {statusLine ? <NavBarStatus title={statusLine}>{statusLine}</NavBarStatus> : null}
                </span>
              ) : null}
            </>
          ) : undefined
        }
        right={actions ?? undefined}
        avatar={{
          name,
          email,
          src: user?.picture || undefined,
          label: accountMenuLabel(name),
          menu: (
            <AccountMenu
              name={name}
              email={email}
              picture={user?.picture || undefined}
              middlewareApi={Config.middlewareApi}
            />
          ),
        }}
      />
    </header>
  );
};

export default styled(OnboardingHeader)`
  /* The bar sits 8 px in from the top and both sides, where the viewer's bar sits (R5.2). */
  flex: none;
  margin: ${space[2]} ${space[2]} 0;

  /*
   * The chip as the Versions trigger (twin-details-page-versions, R1): a bare native button around
   * the chip's own tag, so the tag keeps its look and the button gives it a name, a keyboard and the
   * design's open glow (frame 08's \`liveOpen\`: \`0 0 10px 2px\` in frost).
   */
  .chip-trigger {
    display: inline-flex;
    padding: 0;
    border: none;
    border-radius: ${radius.pill};
    background: transparent;
    cursor: pointer;
    font: inherit;

    &:focus-visible {
      outline: none;
      box-shadow: ${brand.focusRing};
    }

    &.open {
      box-shadow: 0 0 10px 2px ${brand.frostGlow};
    }
  }

  .chip-content {
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
  }

  /* The design's 14/20 semibold name; a long one ends in an ellipsis and keeps the whole name as its title. */
  .twin-name {
    /*
     * At most 320 px. It gives way second, after the status line has gone (the step header's
     * shrink weight below), down to 96 px, still ending in its ellipsis (R1.4). Layout only.
     */
    flex: 0 1 auto;
    min-width: 96px;
    max-width: 320px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: ${typeRole.body1Stronger.fontFamily};
    font-size: ${typeRole.body1Stronger.fontSize};
    line-height: ${typeRole.body1Stronger.lineHeight};
    font-weight: ${typeRole.body1Stronger.fontWeight};
    color: ${semantic.text.primary};
  }

  /*
   * The step's title with its status line stacked under it (onboarding-persistent-scene-step-titles,
   * R1.2-R1.4; ruled 2026-11-01): side by side, the status line was cut at 1440 px on every step with
   * a search. Stacked, the two share one column, so the line needs only its own width.
   *
   * What gives way, in order (R1.4): the status line, to its ellipsis (a shrink weight far above its
   * neighbours', and its minimum is the title's width, so it stops there); then the twin's
   * name, to 96 px. Past that the left slots clip (below) rather than paint under the step's
   * controls. Layout only.
   */
  .step-header {
    /*
     * One grid column: as wide as the whole status line where there is room, and never narrower than
     * the title (the status line brings no minimum of its own).
     */
    display: grid;
    align-content: center;
    flex: 1 1000 auto;
  }

  .step-header h3 {
    flex: none;
    margin: 0;
    white-space: nowrap;
    font-family: ${typeRole.subtitle1.fontFamily};
    font-size: ${typeRole.subtitle1.fontSize};
    line-height: ${typeRole.subtitle1.lineHeight};
    font-weight: ${typeRole.subtitle1.fontWeight};
    color: ${semantic.text.primary};
  }

  /*
   * Flush with the title's left edge, and a 16 px line: the 26 + 16 px pair is centred in the bar's
   * 40 px content box and uses 1 px of its 4 px padding above and below, inside the 52 px bar.
   */
  .step-header .tl-nav-status {
    /* No minimum of its own in the grid, so the step header's minimum is the title's width. */
    min-width: 0;
    padding: 0;
    line-height: ${typeRole.caption1.lineHeight};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /*
   * The step's controls never give way to the status line (R1.4): the nav bar's slot groups both
   * shrink by default, which would let the right group's search spill over the avatar. Layout only.
   */
  .tl-nav-slots.is-right {
    flex-shrink: 0;
  }

  /*
   * The last resort, once the status line has gone and the name is at 96 px (a long chip, a failed
   * draft tag with its reason and Retry, a narrow window): the left slots are cut at their edge
   * instead of running under the right ones. Clipped across only, so the chip's open glow and focus
   * ring keep their height. Layout only.
   */
  .tl-nav-slots.is-left {
    overflow-x: clip;
  }
`;
